import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TraceabilityService } from '../../services/traceability.service';
import { HttpClient } from '@angular/common/http';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TenantsService } from '../../services/tenants.service';
import { Tenant } from '../../model/tenant';
import { Organization } from '../../model/organization';
import { ProcessesService } from '../../services/processes.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-traceability',
  templateUrl: './traceability.component.html',
  styleUrls: ['./traceability.component.scss']
})
export class TraceabilityComponent implements OnInit, OnDestroy {

  public selectedDocuments: any[] = [];
  public loadingDocuments: boolean;
  public totalDocuments: number;
  public rows = 10;
  public first = 0;
  public data: any = { hits: new Array(this.rows) };

  private dataKey = 0;

  public displayStackTrace: boolean;
  public stackTrace: string;

  public displayDataflowDiagram: boolean;

  public filtersVisible = false;
  public filtersForm: FormGroup;

  public tenants: Tenant[];
  public organizations: Organization[];
  public processes: { id: string }[];

  public timeOptions = [
    { time: 10, unit: 'minutes', timeInMinutes: 10 },
    { time: 30, unit: 'minutes', timeInMinutes: 30 },
    { time: 1, unit: 'hours', timeInMinutes: 60 },
    { time: 2, unit: 'hours', timeInMinutes: 120 },
    { time: 3, unit: 'hours', timeInMinutes: 180 },
    { time: 5, unit: 'hours', timeInMinutes: 300 },
    { time: 12, unit: 'hours', timeInMinutes: 720 },
    { time: 18, unit: 'hours', timeInMinutes: 1080 },
    { time: 24, unit: 'hours', timeInMinutes: 1440 },
    { time: 30, unit: 'hours', timeInMinutes: 1800 },
    { time: 36, unit: 'hours', timeInMinutes: 2160 },
  ];

  private tenantChangesSubscription: Subscription | undefined;

  constructor(
    private http: HttpClient,
    private traceabilityService: TraceabilityService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private tenantsService: TenantsService,
    private processesService: ProcessesService
  ) {
  }

  filtersValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const exceptionSearchTerm = control.get('exceptionSearchTerm').value;
    const process = control.get('process').value;
    const timeAgo = control.get('timeAgo').value;
    const tenant = control.get('tenant').value;
    const organization = control.get('organization').value;

    return !!exceptionSearchTerm
        || !!process
        || !!timeAgo
        || !!tenant
        || !!organization
      ? null
      : { allFiltersEmpty: true };
  }

  ngOnInit(): void {
    this.filtersForm = this.fb.group({
      exceptionSearchTerm: [undefined],
      process: [undefined],
      timeAgo: [undefined],
      tenant: [undefined],
      organization: [undefined]
    }, { validators: [this.filtersValidator] });

    this.processes = this.processesService.processes;

    this.tenantsService.getTenants().subscribe(response => {
      this.tenants = response.tenants.sort((a, b) => a.name > b.name ? 1 : -1);
    });

    this.tenantChangesSubscription = this.filtersForm.get('tenant').valueChanges.subscribe((tenantName: string) => {
      this.filtersForm.get('organization').setValue(undefined);
      if (!tenantName){ return; }
      const tenantUuid: string = this.tenants.find(tenant => tenant.name === tenantName).uuid;
      this.tenantsService.getTenantOrganizations(tenantUuid).subscribe(response => {
        this.organizations = response.organizations.sort((a, b) => a.name > b.name ? 1 : -1);
      });
    });

  }

  filterTable(): void {
    this.loadingDocuments = true;

    const process = this.filtersForm.get('process').value;
    const tenant = this.filtersForm.get('tenant').value;
    const organization = this.filtersForm.get('organization').value;
    const exceptionSearchTerm = this.filtersForm.get('exceptionSearchTerm').value;
    const timeAgo = this.filtersForm.get('timeAgo').value;

    const mustFilter = [];
    const filters = [];

    if ( !!process ) {
      mustFilter.push(this.traceabilityService.buildFullMatchCondition('process.keyword', process));
    }

    if ( !!tenant ) {
      mustFilter.push(this.traceabilityService.buildPartialMatchCondition('tenant.keyword', tenant));
    }

    if ( !!organization ) {
      mustFilter.push(this.traceabilityService.buildPartialMatchCondition('organization.keyword', organization));
    }

    if ( !!exceptionSearchTerm ) {
      filters.push(this.traceabilityService.buildSearchTermCondition(exceptionSearchTerm));
    }

    if ( !!timeAgo ) {
      filters.push(this.traceabilityService.buildDateCondition(timeAgo));
    }

    this.selectedDocuments = [];

    this.traceabilityService.retrieveLogs(mustFilter, filters, this.first, this.rows)
      .subscribe((response) => {
        this.data = response.hits;
        this.data.hits.forEach(entry => {
          try {
            entry._source.message = JSON.parse(entry._source.message);
          } catch ( e ) {
            // TODO not a valid JSON -> display as string...
          }
          if ( entry._source.traceId === '' || entry._source.traceId === null ) {
            entry._source.traceId = this.dataKey++;
          }
          if ( !!exceptionSearchTerm ) {
            const regex = new RegExp(`(${exceptionSearchTerm})`, 'gi');
            entry._source.stackTrace = entry._source.stackTrace.replace(regex, '<span class="highlight">$1</span>');
          }
        });
        this.totalDocuments = this.data.total.value;
        setTimeout(() => this.loadingDocuments = false, 300);
      });
    this.displayDataflowDiagram = false;
  }

  onPageChange(event: any): void {
    this.filterTable();
  }

  processSelected(process: string): void {
    this.filtersForm.get('process').setValue(process);
    this.displayDataflowDiagram = false;
  }

  applyFilters(): void {
    this.first = 0;
    this.filterTable();
    this.filtersVisible = false;
  }

  resetFilters(): void {
    this.filtersForm.reset({
      exceptionSearchTerm: '',
      process: '',
      tenant: '',
      organization: ''
    });
    this.filtersVisible = false;
    this.filterTable();
  }

  requeueSelectedDocuments(): void {
    if ( this.selectedDocuments.length !== 0 ) {
      const url = `/catalog/traceability/requeue`;
      this.http.post<any>(url, this.selectedDocuments).toPromise().then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succesfully',
          detail: 'Messages requeued successfully'
        });
        this.selectedDocuments = [];
      });
    }
  }

  showStackTrace(stacktrace): void {
    this.stackTrace = stacktrace;
    this.displayStackTrace = true;
  }

  getType(document): string {
    return `${ document.level } (${ document.type })`;
  }

  showFiltersMenu(): void {
    this.filtersVisible = true;
  }

  showDataFlowDiagram(): void {
    this.displayDataflowDiagram = true;
  }

  ngOnDestroy(): void {
    this.tenantChangesSubscription?.unsubscribe();
  }

}
